import type { Track } from "@similarweb/tracking-provider";

import { TypeOfSelectors } from "app/pages/sales-intelligence/common-components/MultiSelector/types";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import { allTrackers } from "app/services/track/track";

const getExportTypeName = (option: string): string | undefined => {
    if (TypeOfSelectors.EXCEL === option) {
        return "excel";
    }

    if (TypeOfSelectors.EXPORT_SALES_FORCE === option) {
        return "salesforce";
    }

    if (TypeOfSelectors.EXPORT_HUBSPOT === option) {
        return "hubspot";
    }

    if (TypeOfSelectors.EXPORT_DYNAMICS === option) {
        return "dynamics";
    }
};

const createMultiSelectorPanelTrackingService = (track: Track) => {
    const getTrackingOpenEventNameByOption = (option: string) => {
        if (TypeOfSelectors.ACCOUNT === option) {
            return "save to my lists options";
        }
        if (
            TypeOfSelectors.EXCEL === option ||
            TypeOfSelectors.EXPORT_HUBSPOT === option ||
            TypeOfSelectors.EXPORT_SALES_FORCE === option ||
            TypeOfSelectors.EXPORT_DYNAMICS === option
        ) {
            return "export options";
        }
        if (TypeOfSelectors.DELETE === option) {
            return "remove from lists";
        }
    };

    const getTrackingCloseEventNameByOption = (option: string) => {
        if (TypeOfSelectors.ACCOUNT === option) {
            return "save to my lists";
        }
        if (
            TypeOfSelectors.EXCEL === option ||
            TypeOfSelectors.EXPORT_HUBSPOT === option ||
            TypeOfSelectors.EXPORT_SALES_FORCE === option ||
            TypeOfSelectors.EXPORT_DYNAMICS === option
        ) {
            return "export options";
        }
        if (TypeOfSelectors.DELETE === option) {
            return "delete";
        }
    };

    return {
        trackMultiSelectorMenuOpen(quota: number) {
            track("multi select actions", "open menu", createTrackingEventName(quota));
        },
        trackMultiSelectorClose(option: string) {
            track("multi select actions", "close", `${getTrackingCloseEventNameByOption(option) ?? ""}`);
        },
        trackMultiSelectorOpen(option: string) {
            track(
                "multi select actions",
                "show options",
                createTrackingEventName(getTrackingOpenEventNameByOption(option), getExportTypeName(option)),
            );
        },
        trackMultiSelectorExportClick(
            destination: "excel" | "salesforce" | "hubspot" | "dynamics",
            quota: number,
            option: string,
            records: number,
        ) {
            track("multi select actions", `export to ${destination} + ${option}`, `export/${quota}/${records}`);
        },
        trackMultiSelectorSaveToListClick(quota: number, item: SelectionDropdownItem) {
            track("multi select actions", `save + ${item.alias}`, `save to list/${quota}/${item.value}`);
        },
        trackMultiSelectorMoveToListClick() {
            track("multi select actions", "click move", "move to list");
        },
        trackMultiSelectorAssignToOtherUserClick() {
            track("multi select actions", "click assign to", "assign websites");
        },
        trackMultiSelectorSuccessMoveToListAction(domains: readonly string[], selectedListName: string) {
            track("multi select actions", "move to list", `move to another list/${domains.length}/${selectedListName}`);
        },
        trackMultiSelectorSuccessMovedPopup(option: string) {
            track("sites successfully moved", "select option", option);
        },
        trackMultiSelectorRemoveClick(domains: number, selectedDomains: number) {
            track("multi select actions", "click", `remove from list/${domains}/${selectedDomains}`);
        },
        trackMultiSelectorExportToCrm(
            crm: Integration,
            status: "success" | "fail",
            amountDomains: number,
            amountExported: number,
        ) {
            track("multi select actions", `export accounts to ${crm}`, `${status}/${amountDomains}/${amountExported}`);
        },
        trackMultiSelectorExportToCrmClick(quota: number, amountDomains: number, crm: Integration) {
            track("multi select actions", `export accounts to ${crm}`, `export/${quota}/${amountDomains}`);
        },
    };
};

export default createMultiSelectorPanelTrackingService(allTrackers.dispatchTrackEvent.bind(allTrackers));
